import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderToStaticMarkup } from "react-dom/server";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import Hls from "hls.js";
import "maplibre-gl/dist/maplibre-gl.css";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Maximize2, Minimize2, CircleDot, Car } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Device, Position, Driver } from "@/lib/traccar-api";
import { fetchDrivers, fetchGroups, fetchDevices, fetchPermissionsMap, userDevicesFromMap, EDGE_FUNCTION_URL } from "@/lib/traccar-api";
import { useResellerAccess } from "@/hooks/useResellerAccess";
import { fetchCustomers } from "@/lib/reseller-api";
import { EditDeviceDialog } from "@/components/devices/EditDeviceDialog";
import { type CommunicationFilter } from "./LastCommunicationCard";
import { differenceInHours } from "date-fns";
import type { Locale } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { useMapInit } from "@/hooks/useMapInit";
import { DeviceRowContent } from "@/components/devices/DeviceRowContent";
import { get3DIconUrl } from "@/data/car-brands";
import { MapStylePicker } from "@/components/map/MapStylePicker";

export type DeviceStatusFilter = 'moving' | 'idle' | 'parked' | 'offline' | null;

const CLUSTER_ZOOM_THRESHOLD = 10; // Show clusters below this zoom, markers above

interface DeviceMapProps {
  devices: Device[];
  positions: Position[];
  focusPosition?: Position | null;
  statusFilter?: DeviceStatusFilter;
  communicationFilter?: CommunicationFilter;
  selectedDeviceId?: number | null;
  onDeviceSelect?: (deviceId: number | null) => void;
  className?: string;
  compact?: boolean;
  controlsContainer?: HTMLElement | null;
}

function buildPopupContent(device: Device, position: Position, t: (key: string) => string, _drivers?: Driver[], locale?: Locale, customerName?: string) {
  const streetViewUrl = `https://street-view.entrack-plataforma.workers.dev/?heading=${position.course || 0}&location=${position.latitude},${position.longitude}&size=260x130&return_error_code=true`;
  const streetViewLink = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course}`;

  // Camera detection — same attribute keys as Cameras.tsx
  const devAttrs = device.attributes || {};
  const cameraRawUrl = (['cameraUrl', 'streamUrl', 'camera', 'videoUrl'] as const)
    .map(k => devAttrs[k])
    .find((v): v is string => typeof v === 'string' && v.trim().length > 0)
    ?.trim();

  const speedKmh = Math.round(position.speed * 1.852);
  const isOnline = device.status === 'online';
  const isMoving = isOnline && position.speed > 0;
  const ignition = position.attributes?.ignition as boolean | undefined;
  const statusColor = isMoving ? '#22c55e' : (isOnline && ignition) ? '#eab308' : isOnline ? '#f97316' : '#ef4444';
  const course = position.course || 0;

  const isDark = document.documentElement.classList.contains('dark');
  const bg = isDark ? '#1c1c1e' : '#ffffff';
  const border = isDark ? '#333' : '#eee';
  const linkColor = isDark ? '#aaa' : '#666';

  const navArrow = isMoving
    ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform:rotate(${course - 45}deg);flex-shrink:0"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>`
    : '';

  const infoHtml = renderToStaticMarkup(
    <DeviceRowContent device={device} position={position} locale={locale} indent={false} wrap={false} t={t} />
  );

  const navigateLink = `https://www.google.com/maps/dir/?api=1&destination=${position.latitude},${position.longitude}`;

  const btnStyle = `flex:1;text-align:center;padding:6px 2px;border-radius:6px;font-size:10px;color:${linkColor};text-decoration:none;cursor:pointer;border:none;background:transparent;line-height:1.2`;

  const customerLine = customerName ? `<div style="font-size:10px;color:rgba(255,255,255,0.85);text-shadow:0 1px 3px rgba(0,0,0,0.5);margin-top:1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${customerName}</div>` : '';
  const nameOverlay = `<div style="position:absolute;top:0;left:0;right:0;padding:6px 10px;background:linear-gradient(rgba(0,0,0,0.7),transparent);border-radius:4px 4px 0 0;z-index:1"><div style="display:flex;align-items:center;gap:6px"><span style="width:9px;height:9px;border-radius:50%;background:${statusColor};flex-shrink:0;box-shadow:0 0 0 2px rgba(255,255,255,0.3)"></span><span style="font-weight:bold;font-size:13px;color:white;text-shadow:0 1px 3px rgba(0,0,0,0.5);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${device.name}</span>${isOnline ? `<span style="font-size:11px;color:white;font-family:monospace;display:flex;align-items:center;gap:3px;flex-shrink:0;text-shadow:0 1px 3px rgba(0,0,0,0.5)">${navArrow}${speedKmh} km/h</span>` : ''}</div>${customerLine}</div>`;
  const cameraIcoSvg = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M23 7 16 12 23 17V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`;

  let topSection: string;
  if (cameraRawUrl) {
    const cameraUser = typeof devAttrs.cameraUser === 'string' ? devAttrs.cameraUser.trim() : '';
    const cameraPass = typeof devAttrs.cameraPassword === 'string' ? devAttrs.cameraPassword.trim() : '';
    let authUrl = cameraRawUrl;
    if (cameraUser && (cameraRawUrl.startsWith('rtsp://') || cameraRawUrl.startsWith('rtsps://'))) {
      const scheme = cameraRawUrl.startsWith('rtsps://') ? 'rtsps' : 'rtsp';
      const rest = cameraRawUrl.slice(scheme.length + 3);
      authUrl = `${scheme}://${encodeURIComponent(cameraUser)}:${encodeURIComponent(cameraPass)}@${rest}`;
    }
    const isRtsp = cameraRawUrl.startsWith('rtsp://') || cameraRawUrl.startsWith('rtsps://');
    const isInsecure = !cameraRawUrl.startsWith('https://');
    const isHls = cameraRawUrl.includes('.m3u8');

    const fsBtn = `<button data-action="cam-fullscreen" style="position:absolute;bottom:6px;right:6px;z-index:2;background:rgba(0,0,0,0.55);border:none;border-radius:4px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:white;padding:0"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg></button>`;

    if (isRtsp) {
      topSection = `<div data-camera="1" style="position:relative;overflow:hidden;border-radius:4px 4px 0 0;background:#111;height:130px">${nameOverlay}${fsBtn}<div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:rgba(255,255,255,0.6);font-size:11px">${cameraIcoSvg}<a href="${authUrl}" style="color:rgba(255,255,255,0.5);text-decoration:underline;font-size:10px">Open in player</a></div></div>`;
    } else if (isInsecure) {
      topSection = `<a data-camera="1" target="_blank" rel="noreferrer" href="${cameraRawUrl}" style="position:relative;display:block;overflow:hidden;border-radius:4px 4px 0 0;background:#111;height:130px;text-decoration:none">${nameOverlay}${fsBtn}<div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:rgba(255,255,255,0.6);font-size:11px">${cameraIcoSvg}<span>Open stream</span></div></a>`;
    } else if (isHls) {
      topSection = `<div data-camera="1" style="position:relative;overflow:hidden;border-radius:4px 4px 0 0;background:#000;height:130px">${nameOverlay}${fsBtn}<video data-hls-src="${cameraRawUrl}" width="260" height="130" autoplay muted playsinline style="display:block;width:100%;height:130px;object-fit:contain"></video></div>`;
    } else {
      topSection = `<div data-camera="1" style="position:relative;overflow:hidden;border-radius:4px 4px 0 0;background:#000;height:130px">${nameOverlay}${fsBtn}<iframe src="${cameraRawUrl}" width="260" height="130" allowfullscreen style="display:block;width:100%;height:130px;border:0;overflow:hidden"></iframe></div>`;
    }
  } else {
    topSection = `<a target="_blank" rel="noreferrer" href="${streetViewLink}" style="position:relative;display:block;outline:none">${nameOverlay}<!--suppress HtmlDeprecatedAttribute --><img src="${streetViewUrl}" width="260" height="130" alt="Street View" style="display:block;width:100%;border-radius:4px 4px 0 0" onerror="this.parentElement.remove()" /></a>`;
  }

  return `
    <div style="width:260px;background:${bg};border-radius:8px">
      ${topSection}
      <div style="padding:8px 10px 6px">
        ${infoHtml}
      </div>
        <!--suppress CssInvalidPropertyValue -->
        <div style="display:flex;border-top:1px solid ${border};padding:4px 4px 2px;gap:1px;flex-wrap:wrap">
        <a href="${navigateLink}" target="_blank" rel="noreferrer" style="${btnStyle}" title="${t('map.navigate')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
        </a>
        <button data-action="share" style="${btnStyle}" title="${t('map.shareLocation')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        </button>
        <a href="/history/trips?deviceId=${device.id}" style="${btnStyle}" title="${t('map.viewTrips')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>
        </a>
        <button data-action="geofence" style="${btnStyle}" title="${t('map.createGeofence')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
        </button>
        <button data-action="edit" style="${btnStyle}" title="${t('common.edit')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button data-action="block" style="${btnStyle}" title="${t('map.blockEngine')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:0 auto"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </button>
      </div>
    </div>
  `;
}
const rotationFrames = 16

export const DeviceMap = ({ devices, positions, focusPosition, statusFilter, communicationFilter, selectedDeviceId, onDeviceSelect, className, compact = true, controlsContainer }: DeviceMapProps) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isReseller } = useResellerAccess();
  const navigate = useNavigate();
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogDevice, setShareDialogDevice] = useState<Device | null>(null);
  const defaultExpiration = () => {
    const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  const [shareExpiration, setShareExpiration] = useState(defaultExpiration);
  const openShareDialogRef = useRef<((device: Device) => void) | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const markerDeviceIdMap = useRef<Map<maplibregl.Marker, number>>(new Map());
  const hasFittedBounds = useRef(false);
  const lastFilterRef = useRef<string | null>(null);
  const previousPositionsRef = useRef<Map<number, { lat: number; lng: number, fixTime: string }>>(new Map());
  const positionsRef = useRef<Position[]>(positions);
  positionsRef.current = positions;
  const devicesRef = useRef<Device[]>(devices);
  devicesRef.current = devices;
  const [driversData, setDriversData] = useState<Driver[]>([]);
  const driversRef = useRef<Driver[]>([]);
  driversRef.current = driversData;
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(11);
  const [clusteringEnabled, setClusteringEnabled] = useState(true);
  const [use3DIcons, setUse3DIcons] = useState(true);
  const use3DIconsRef = useRef(true);
  const [clusterSourceAdded, setClusterSourceAdded] = useState(false);

  // Keep share dialog opener ref in sync with current state setters
  openShareDialogRef.current = (device: Device) => {
    setShareExpiration(defaultExpiration());
    setShareDialogDevice(device);
    setShareDialogOpen(true);
  };

  // Handle fullscreen toggle — navigates to /map on iOS (no Fullscreen API)
  const toggleFullscreen = async () => {
    const element = mapWrapperRef.current;
    if (!element) return;

    if (!document.fullscreenEnabled) {
      if (window.location.pathname === '/map') {
        navigate('/');
      } else {
        navigate('/map');
      }
      return;
    }

    try {
      if (!document.fullscreenElement) {
        await element.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  // Device → customer name map (reseller only)
  const deviceCustomerMapRef = useRef<Map<number, string>>(new Map());

  // Fetch drivers and groups for popup display
  useEffect(() => {
    fetchDrivers().then(setDriversData).catch(() => {});
    fetchGroups().then(setGroups).catch(() => {});
  }, []);

  // Fetch customer-device mapping for resellers
  useEffect(() => {
    if (!isReseller) return;
    (async () => {
      try {
        const [customers, allDevices, perms] = await Promise.all([
          fetchCustomers(),
          fetchDevices(),
          fetchPermissionsMap(),
        ]);
        const map = new Map<number, string>();
        for (const c of customers.filter(c => c.traccar_user_id)) {
          const devs = userDevicesFromMap(c.traccar_user_id!, allDevices, perms);
          for (const d of devs) map.set(d.id, c.name);
        }
        deviceCustomerMapRef.current = map;
      } catch { /* ignore */ }
    })();
  }, [isReseller]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Resize map after fullscreen change
      setTimeout(() => {
        map.current?.resize();
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const { map, currentStyle, handleStyleChange } = useMapInit(mapContainer, {
    controlsPosition: "bottom-right",
    onBeforeStyleChange: () => setClusterSourceAdded(false),
  });

  // on mount
  useEffect(() => {
    if (!map.current) return;

    map.current.on('zoom', () => {
      if (map.current) {
        setCurrentZoom(map.current.getZoom());
      }
    });

    // Disable marker glide during map moves (pan/zoom), re-enable when done
    map.current.on('movestart', () => {
      mapContainer.current?.classList.remove('map-glide');
    });
    map.current.on('moveend', () => {
      mapContainer.current?.classList.add('map-glide');
    });
    // Start with glide enabled
    mapContainer.current.classList.add('map-glide');

    // Add cluster source and layers once on map styledata
    map.current.on('styledata', () => {
      if (!map.current || map.current.getSource('device-clusters')) return;

      // Add empty GeoJSON source with clustering enabled
      map.current.addSource('device-clusters', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: CLUSTER_ZOOM_THRESHOLD - 1,
        clusterRadius: 50
      });

      // Cluster circles layer
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'device-clusters',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': 'hsla(221, 83%, 53%, 0.9)',
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 20, 30],
          'circle-stroke-width': 2,
          'circle-stroke-color': 'hsla(221, 83%, 53%, 1)'
        }
      });
      // Single device circles layer (unclustered points)
      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'device-clusters',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'status'],
            'moving', '#22c55e',  // green - moving
            'idle', '#eab308',    // yellow - idle
            'parked', '#f97316',  // orange - parked
            '#ef4444'             // red - offline (default)
          ],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Cluster count labels
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'device-clusters',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-allow-overlap': true,
          'icon-allow-overlap': true
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Click on cluster to zoom in
      map.current.on('click', 'clusters', async (e) => {
        const features = map.current?.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features?.length) return;
        const clusterId = features[0].properties?.cluster_id;
        const source = map.current?.getSource('device-clusters') as maplibregl.GeoJSONSource;
        try {
          const zoom = await source?.getClusterExpansionZoom(clusterId);
          if (zoom === undefined) return;
          const geometry = features[0].geometry;
          if (geometry.type === 'Point') {
            map.current?.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom
            });
          }
        } catch (err) {
          console.error('Error getting cluster expansion zoom:', err);
        }
      });

      // Cluster hover popup
      let clusterPopup: maplibregl.Popup | null = null;

      map.current.on('mouseenter', 'clusters', async (e) => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = 'pointer';

        const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features?.length) return;

        const feature = features[0];
        const geometry = feature.geometry;
        if (geometry.type !== 'Point') return;

        const coordinates = geometry.coordinates.slice() as [number, number];
        const clusterId = feature.properties?.cluster_id;

        // Get device details from cluster leaves
        const source = map.current.getSource('device-clusters') as maplibregl.GeoJSONSource;
        const statusGroups: { moving: string[]; idle: string[]; parked: string[]; offline: string[] } = {
          moving: [],
          idle: [],
          parked: [],
          offline: []
        };

        try {
          const leaves = await source.getClusterLeaves(clusterId, 250, 0);
          leaves.forEach((leaf) => {
            const props = leaf.properties as Record<string, unknown> | null;
            const name = (props?.name as string) || 'Unknown';
            const status = (props?.status as string) || 'offline';
            if (status === 'moving') statusGroups.moving.push(name);
            else if (status === 'idle') statusGroups.idle.push(name);
            else if (status === 'parked') statusGroups.parked.push(name);
            else statusGroups.offline.push(name);
          });
        } catch (err) {
          console.error('Error getting cluster leaves:', err);
        }

        const maxPerGroup = 6;

        const dk = document.documentElement.classList.contains('dark');
        const cBg = dk ? '#1c1c1e' : '#ffffff';
        const cText = dk ? '#e5e5e5' : '#1a1a1a';
        const cMuted = dk ? '#888' : '#666';

        const renderGroup = (labelKey: string, color: string, names: string[]) => {
          if (names.length === 0) return '';
          const displayNames = names.slice(0, maxPerGroup);
          const remaining = names.length - displayNames.length;
          return `
            <div>
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                <span style="width: 8px; height: 8px; border-radius: 50%; background: ${color};"></span>
                <span style="font-weight: 600; font-size: 12px; color: ${cText}">${t(labelKey)} (${names.length})</span>
              </div>
              <ul style="margin: 0; padding-left: 20px; font-size: 12px; color: ${cMuted};">
                ${displayNames.map(name => `<li style="margin: 1px 0;">${name}</li>`).join('')}
                ${remaining > 0 ? `<li style="font-style: italic;">+${remaining} ${t('map.more')}</li>` : ''}
              </ul>
            </div>
          `;
        };

        const popupContent = `
          <div style="padding: 12px; background: ${cBg}; border-radius: 8px; overflow-y: auto;">
            ${renderGroup('map.moving', '#22c55e', statusGroups.moving)}
            ${renderGroup('map.idle', '#eab308', statusGroups.idle)}
            ${renderGroup('map.parked', '#f97316', statusGroups.parked)}
            ${renderGroup('map.offline', '#ef4444', statusGroups.offline)}
          </div>
        `;
        if (clusterPopup) {
          clusterPopup.remove();
          clusterPopup = null;
        }
        clusterPopup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 15,
          className: 'no-glide'
        })
          .setLngLat(coordinates)
          .setHTML(popupContent)
          .addTo(map.current);
      });

      map.current.on('mouseleave', 'clusters', () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
        if (clusterPopup) {
          clusterPopup.remove();
          clusterPopup = null;
        }
      });
      // Click on an unclustered point to select a device and show a popup
      map.current.on('click', 'unclustered-point', (e) => {
        if (!map.current || !e.features?.length) return;
        const feature = e.features[0];
        const geometry = feature.geometry;
        if (geometry.type !== 'Point') return;

        const props = feature.properties;
        const deviceId = props?.deviceId;

        const device = devicesRef.current.find(d => d.id === deviceId);
        if (!device) return;

        // Notify parent of device selection
        if (deviceId && onDeviceSelect) {
          onDeviceSelect(deviceId);
        }
      });
      map.current.on('mouseenter', 'unclustered-point', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'unclustered-point', () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
      });

      setClusterSourceAdded(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize map when maximized state changes
  useEffect(() => {
    if (!map.current) return;
    const timeout = setTimeout(() => {
      map.current?.resize();
    }, 350);
    return () => clearTimeout(timeout);
  }, [isMaximized]);

  // main updates
  useEffect(() => {
    if (!map.current) return;

    // Show/hide markers based on zoom level and clustering setting
    const showMarkers = !clusteringEnabled || currentZoom >= CLUSTER_ZOOM_THRESHOLD;

    const devicesMap = new Map<number, Device>();
    devices.forEach(d => devicesMap.set(d.id, d));

    // Filter positions based on status filter and communication filter
    const now = new Date();
    const filteredPositions = positions.filter((position) => {
      const device = devicesMap.get(position.deviceId);
      if (!device) return false;

      // Apply status filter
      if (statusFilter) {
        const isOnline = device.status === 'online';
        const isMoving = position.speed > 0;

        const ignition = position.attributes?.ignition as boolean | undefined;
        switch (statusFilter) {
          case 'moving':
            if (!(isOnline && isMoving)) return false;
            break;
          case 'idle':
            if (!(isOnline && !isMoving && ignition)) return false;
            break;
          case 'parked':
            if (!(isOnline && !isMoving && !ignition)) return false;
            break;
          case 'offline':
            if (isOnline) return false;
            break;
        }
      }

      // Apply communication filter
      if (communicationFilter === 'noposition') {
        if (device.lastUpdate) return false;
      } else if (communicationFilter && device.lastUpdate && device.positionId) {
        const lastUpdate = new Date(device.lastUpdate);
        const hoursAgo = differenceInHours(now, lastUpdate);

        switch (communicationFilter) {
          case 'recent':
            if (hoursAgo >= 1) return false;
            break;
          case 'warning':
            if (hoursAgo < 1 || hoursAgo >= 24) return false;
            break;
          case 'critical':
            if (hoursAgo < 24) return false;
            break;
        }
      } else if (communicationFilter === 'critical' && !device.lastUpdate) {
        return true;
      } else if (communicationFilter && (!device.lastUpdate || !device.positionId)) {
        return false;
      }

      return true;
    });

    // Fit bounds when filter changes
    const currentFilterKey = `${statusFilter || ''}-${communicationFilter || ''}`;
    if (currentFilterKey !== lastFilterRef.current && (statusFilter || communicationFilter)) {
      lastFilterRef.current = currentFilterKey;

      if (filteredPositions.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        filteredPositions.forEach(pos => {
          bounds.extend([pos.longitude, pos.latitude]);
        });
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 15, duration: 500 });
      }
    } else if (!statusFilter && !communicationFilter && lastFilterRef.current) {
      // Reset when filters are cleared
      lastFilterRef.current = null;
    }

    // Update cluster source data (only if source is ready)
    if (clusterSourceAdded) {
      const source = map.current.getSource('device-clusters') as maplibregl.GeoJSONSource;
      if (source) {
        const geojsonData: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: filteredPositions.map((pos) => {
            const device = devicesMap.get(pos.deviceId);
            const isOnline = device?.status === 'online';
            const isMoving = pos.speed > 0;
            // Determine status for color matching
            const ignition = pos.attributes?.ignition as boolean | undefined;
            let status = 'offline';
            if (isOnline && isMoving) status = 'moving';
            else if (isOnline && ignition) status = 'idle';
            else if (isOnline) status = 'parked';

            return {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [pos.longitude, pos.latitude] },
              properties: { deviceId: pos.deviceId, name: device?.name || 'Unknown', status, course: pos.course || 0 }
            };
          })
        };
        source.setData(geojsonData);
      }
      // Toggle cluster layer visibility
      if (map.current.getLayer('clusters')) {
        map.current.setLayoutProperty('clusters', 'visibility', showMarkers ? 'none' : 'visible');
      }
      if (map.current.getLayer('cluster-count')) {
        map.current.setLayoutProperty('cluster-count', 'visibility', showMarkers ? 'none' : 'visible');
      }
      if (map.current.getLayer('unclustered-point')) {
        map.current.setLayoutProperty('unclustered-point', 'visibility', showMarkers ? 'none' : 'visible');
      }
    }

    // Hide markers when zoomed out (showing clusters)
    if (!showMarkers) {
      markersRef.current.forEach(m => m.getElement().style.display = 'none');
      return;
    }

    // Create a map of existing markers by deviceId for reuse
    const existingMarkers = new Map<number, maplibregl.Marker>();
    markersRef.current.forEach((marker) => {
      const deviceId = markerDeviceIdMap.current.get(marker);
      if (deviceId !== undefined) existingMarkers.set(deviceId, marker);
    });

    const newMarkers: maplibregl.Marker[] = [];

    const wirePopupHandlers = (popupEl: Element | null, device: Device, position: Position) => {
      if (!popupEl) return;
      const isDark = document.documentElement.classList.contains('dark');
      const hoverBg = isDark ? '#2a2a2c' : '#f5f5f5';

      popupEl.querySelectorAll('[data-action]').forEach((el) => {
        const btn = el as HTMLElement;
        btn.addEventListener('mouseenter', () => { btn.style.background = hoverBg; });
        btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
      });

      const shareBtn = popupEl.querySelector('[data-action="share"]') as HTMLElement | null;
      if (shareBtn) {
        shareBtn.addEventListener('click', () => {
          openShareDialogRef.current?.(device);
        });
      }

      const geoBtn = popupEl.querySelector('[data-action="geofence"]') as HTMLElement | null;
      if (geoBtn) {
        geoBtn.addEventListener('click', () => {
          const orig = geoBtn.innerHTML;
          geoBtn.textContent = '...';
          fetch('/api/geofences', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: device.name, area: `CIRCLE (${position.latitude} ${position.longitude}, 100)` }),
          }).then(r => { if (r.ok) return r.json(); throw new Error(); })
            .then(g => fetch('/api/permissions', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deviceId: position.deviceId, geofenceId: g.id }) }))
            .then(() => { geoBtn.textContent = 'Created!'; setTimeout(() => { geoBtn.innerHTML = orig; }, 1500); })
            .catch(() => { geoBtn.textContent = 'Error'; setTimeout(() => { geoBtn.innerHTML = orig; }, 1500); });
        });
      }

      const blockBtn = popupEl.querySelector('[data-action="block"]') as HTMLElement | null;
      if (blockBtn) {
        blockBtn.addEventListener('click', () => {
          if (!confirm(t('map.blockEngineConfirm'))) return;
          const orig = blockBtn.innerHTML;
          blockBtn.textContent = '...';
          fetch(`${EDGE_FUNCTION_URL}/api/commands/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ deviceId: position.deviceId, type: 'engineStop' }),
          }).then(r => { if (r.ok) return; throw new Error(); })
            .then(() => { blockBtn.textContent = 'Sent!'; setTimeout(() => { blockBtn.innerHTML = orig; }, 2000); })
            .catch(() => { blockBtn.textContent = 'Error'; setTimeout(() => { blockBtn.innerHTML = orig; }, 2000); });
        });
      }

      const editBtn = popupEl.querySelector('[data-action="edit"]') as HTMLElement | null;
      if (editBtn) {
        editBtn.addEventListener('click', () => { setEditingDevice(device); setEditDialogOpen(true); });
      }
    };

    // Add markers for each device position
    filteredPositions.forEach((position) => {

      const device = devicesMap.get(position.deviceId);
      if (!device) return;

      const isOnline = device.status === "online";
      const isMoving = position.speed > 0;

      // Determine marker color
      const ignition = position.attributes?.ignition as boolean | undefined;
      let color = "#ef4444"; // red - offline
      if (isOnline && isMoving) color = "#22c55e"; // green - moving
      else if (isOnline && ignition) color = "#eab308"; // yellow - idle (ignition on)
      else if (isOnline) color = "#f97316"; // orange - parked (ignition off)

      // Check if the position changed BEFORE updating the ref
      const prevPos = previousPositionsRef.current.get(position.deviceId);
      const hasPositionChanged = prevPos && (prevPos.lat !== position.latitude || prevPos.lng !== position.longitude || prevPos.fixTime !== position.fixTime);

      // Update previous position ref
      previousPositionsRef.current.set(position.deviceId, {
        lat: position.latitude,
        lng: position.longitude,
        fixTime: position.fixTime,
      });

      // Check if a marker already exists for this device
      const existingMarker = existingMarkers.get(position.deviceId);
      const rotate = position.course -
          Math.floor(position.course / (360 / rotationFrames)) *
          (360 / rotationFrames);
      if (existingMarker) {
        // Show the marker
        existingMarker.getElement().style.display = '';

        existingMarker.setLngLat([position.longitude, position.latitude]);

        const existingPopup = existingMarker.getPopup();
        if (existingPopup && existingPopup.isOpen() && hasPositionChanged) {
          existingPopup.setHTML(buildPopupContent(device, position, t, driversRef.current, dateFnsLocale, deviceCustomerMapRef.current.get(device.id)));
          wirePopupHandlers(existingPopup.getElement(), device, position);
        }

        const existingEl = existingMarker.getElement();
        const dotEl = (existingEl.querySelector(".fleet-marker-dot") as HTMLElement | null) ?? existingEl;

        if (!use3DIconsRef.current) {
          dotEl.style.backgroundColor = color;
        }

        // Update rotation
        const arrow = dotEl.querySelector(".direction-arrow") as HTMLElement | null;
        const image = dotEl.querySelector("img") as HTMLImageElement | null;
        if (isMoving && arrow) {
          arrow.style.transform = `rotate(${position.course || 0}deg)`;
          arrow.style.opacity = "1";
        } else if (arrow) {
          arrow.style.opacity = "0";
        } else if (image) {
          image.src = get3DIconUrl(device.category ?? '', position.course, color);
          image.style.transform = `rotate(${rotate}deg)`;
        }

        // Trigger pulse animation on position change (disabled for 3D icons)
        if (hasPositionChanged && !use3DIconsRef.current) {
          dotEl.style.animation = "none";
          void dotEl.offsetHeight; // Force reflow
          dotEl.style.animation = "marker-pulse 3s ease-out";

          // If this device has an open popup, pan map to keep it in view
          if (selectedDeviceId === position.deviceId && map.current) {
            const bounds = map.current.getBounds();
            const lngLat = new maplibregl.LngLat(position.longitude, position.latitude);
            if (!bounds?.contains(lngLat)) {
              map.current.panTo(lngLat, { duration: 500 });
            }
          }
        }

        newMarkers.push(existingMarker);
        existingMarkers.delete(position.deviceId);
      } else {
        // Create a marker container - no fixed size, label is positioned absolutely
        const el = document.createElement("div");
        el.className = "fleet-marker";
        const dotSize = 28;
        el.style.position = "absolute";
        el.style.cursor = "pointer";

        // Create inner circle
        let dot
        if (!use3DIconsRef.current) {
          dot = document.createElement("div");
          dot.className = "fleet-marker-dot";
          dot.style.width = `${dotSize}px`;
          dot.style.height = `${dotSize}px`;
          dot.style.borderRadius = "50%";
          dot.style.backgroundColor = color;
          dot.style.border = "3px solid white";
          dot.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
          dot.style.willChange = "transform, box-shadow";
          dot.style.display = "flex";
          dot.style.alignItems = "center";
          dot.style.justifyContent = "center";
          dot.style.position = "relative";

          // Add arrow inside circle for direction
          const arrow = document.createElement("div");
          arrow.className = "direction-arrow";
          arrow.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5l0 14"/><path d="M18 11l-6-6-6 6"/></svg>`;
          arrow.style.transform = `rotate(${position.course || 0}deg)`;
          arrow.style.opacity = isMoving ? "1" : "0";
          arrow.style.transition = "transform 0.3s ease, opacity 0.3s ease";
          arrow.style.display = "flex";
          arrow.style.alignItems = "center";
          arrow.style.justifyContent = "center";
          dot.appendChild(arrow);
        } else {
          // 3D vehicle icon
          dot = document.createElement("img");
          dot.src = get3DIconUrl(device.category ?? '', position.course, color);
          dot.style.transform = `rotate(${rotate}deg)`;
        }

        // Add label below marker - positioned absolutely to not affect container size
        const label = document.createElement("div");
        label.className = "fleet-marker-label";
        label.textContent = device.name;
        label.style.position = "absolute";
        label.style.top = "100%";
        label.style.left = "50%";
        label.style.transform = "translateX(-50%)";
        label.style.marginTop = "4px";
        label.style.padding = "2px 6px";
        label.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
        label.style.borderRadius = "4px";
        label.style.fontSize = "11px";
        label.style.fontWeight = "500";
        label.style.color = "#333";
        label.style.whiteSpace = "nowrap";
        label.style.boxShadow = "0 1px 3px rgba(0,0,0,0.2)";
        label.style.maxWidth = "100px";
        label.style.overflow = "hidden";
        label.style.textOverflow = "ellipsis";

        el.appendChild(dot);
        el.appendChild(label);

        const newPopup = new maplibregl.Popup({ offset: 25, maxWidth: '260px', closeButton: true, closeOnClick: true, className: 'no-glide' }).setHTML(buildPopupContent(device, position, t, driversRef.current, dateFnsLocale, deviceCustomerMapRef.current.get(device.id)));

        newPopup.on('open', () => {
          // Rebuild popup content on open so async data (e.g. customer name) is always fresh
          newPopup.setHTML(buildPopupContent(device, position, t, driversRef.current, dateFnsLocale, deviceCustomerMapRef.current.get(device.id)));
          label.style.display = 'none';
          newPopup.getElement()?.classList.remove('no-glide');
          const popupEl = newPopup.getElement();
          wirePopupHandlers(popupEl, device, position);

          // Trim overflowing indicators and append ellipsis
          const indicatorsEl = popupEl?.querySelector('[data-indicators]') as HTMLElement | null;
          if (indicatorsEl && indicatorsEl.scrollWidth > indicatorsEl.clientWidth) {
            const children = Array.from(indicatorsEl.children) as HTMLElement[];
            while (children.length > 0 && indicatorsEl.scrollWidth > indicatorsEl.clientWidth) {
              children.pop()?.remove();
            }
            const ellipsis = document.createElement('span');
            ellipsis.textContent = '…';
            ellipsis.style.cssText = 'flex-shrink:0;font-size:11px;color:#888';
            indicatorsEl.appendChild(ellipsis);
          }

          const fsBtnEl = popupEl?.querySelector('[data-action="cam-fullscreen"]') as HTMLElement | null;
          if (fsBtnEl) {
            fsBtnEl.addEventListener('click', (e) => {
              e.stopPropagation();
              if (document.fullscreenElement) {
                document.exitFullscreen();
              } else {
                const cam = popupEl?.querySelector('[data-camera]') as HTMLElement | null;
                cam?.requestFullscreen?.();
              }
            });
          }
          const hlsVideo = popupEl?.querySelector('video[data-hls-src]') as HTMLVideoElement | null;
          if (hlsVideo) {
            const hlsSrc = hlsVideo.dataset.hlsSrc!;
            if (hlsVideo.canPlayType('application/vnd.apple.mpegurl')) {
              hlsVideo.src = hlsSrc;
            } else if (Hls.isSupported()) {
              const hls = new Hls();
              hls.loadSource(hlsSrc);
              hls.attachMedia(hlsVideo);
              newPopup.once('close', () => hls.destroy());
            }
          }
        });
        newPopup.on('close', () => {
          label.style.display = '';
          newPopup.getElement()?.classList.add('no-glide');
          if (onDeviceSelect) {
            onDeviceSelect(null);
          }
        });

        // Anchor at center of the dot element
        const marker = new maplibregl.Marker({
          element: el,
          anchor: 'center'
        })
          .setLngLat([position.longitude, position.latitude])
          .setPopup(newPopup)
          .addTo(map.current!);

        // Add click handler to select device & close other popups
        el.addEventListener('click', () => {
          if (onDeviceSelect) {
            onDeviceSelect(position.deviceId);
          }
        });

        markerDeviceIdMap.current.set(marker, position.deviceId);
        newMarkers.push(marker);
      }
    });

    // Remove markers that no longer have positions
    existingMarkers.forEach((marker) => marker.remove());
    markersRef.current = newMarkers;

    // Fit bounds to show all markers only on initial load
    if (positions.length > 0 && !hasFittedBounds.current) {
      const bounds = new maplibregl.LngLatBounds();
      positions.forEach((pos) => bounds.extend([pos.longitude, pos.latitude]));
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 14 });
      hasFittedBounds.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, statusFilter, currentZoom, clusteringEnabled, clusterSourceAdded, use3DIcons]);

  // Fly to focused position when clicked from history
  useEffect(() => {
    if (!map.current || !focusPosition) return;

    map.current.flyTo({
      center: [focusPosition.longitude, focusPosition.latitude],
      zoom: 16,
      duration: 1000,
    });
  }, [focusPosition]);


  useEffect(() => { // Center map on selected device and open its popup
    if (!map.current || !selectedDeviceId) return;

    const position = positionsRef.current.find(p => p.deviceId === selectedDeviceId);
    if (position) {
      map.current.flyTo({
        center: [position.longitude, position.latitude],
        zoom: Math.max(map.current.getZoom(), 14),
        duration: 800,
      });

      // Close other popups, then open this device's popup after flying
      markersRef.current.forEach(m => {
        const popup = m.getPopup();
        if (markerDeviceIdMap.current.get(m) !== selectedDeviceId && popup?.isOpen()) {
          popup.remove();
        }
      });

      map.current.once('moveend', () => {
        const marker = markersRef.current.find(m => markerDeviceIdMap.current.get(m) === selectedDeviceId);
        if (marker && !marker.getPopup()?.isOpen()) {
          marker.togglePopup();
          setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
        }
      });
    }
  }, [selectedDeviceId]);

  // Add CSS for marker pulse animation
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "fleet-marker-styles";
    style.textContent = `
      @media (min-width: 640px) {
        @keyframes marker-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7), 0 2px 6px rgba(0,0,0,0.3);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 12px rgba(34, 197, 94, 0), 0 2px 6px rgba(0,0,0,0.3);
            transform: scale(1.2);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0), 0 2px 6px rgba(0,0,0,0.3);
            transform: scale(1);
          }
        }
      }
      .fleet-marker-dot {
        transition: background-color 0.3s ease;
        transform-origin: center center;
      }
      .map-glide .maplibregl-marker,
      .map-glide .maplibregl-popup:not(.no-glide) {
        transition: transform 2s ease-out;
      }      
      .maplibregl-popup-content { 
        position: relative;
        padding: 0;
        background: transparent;
        box-shadow: 0 3px 14px rgba(0,0,0,0.25);
        border-radius: 8px;
        overflow: hidden;
      }
      .maplibregl-popup-tip {
        border-top-color: #ffffff;
      }
      .dark .maplibregl-popup-tip {
        border-top-color: #1c1c1e;
      }
      .maplibregl-popup-anchor-bottom .maplibregl-popup-tip {
        border-top-color: #ffffff;
      }
      .dark .maplibregl-popup-anchor-bottom .maplibregl-popup-tip {
        border-top-color: #1c1c1e;
      }
      .maplibregl-popup-anchor-top .maplibregl-popup-tip {
        border-bottom-color: #ffffff;
      }
      .dark .maplibregl-popup-anchor-top .maplibregl-popup-tip {
        border-bottom-color: #1c1c1e;
      }
      .maplibregl-popup-anchor-left .maplibregl-popup-tip {
        border-right-color: #ffffff;
      }
      .dark .maplibregl-popup-anchor-left .maplibregl-popup-tip {
        border-right-color: #1c1c1e;
      }
      .maplibregl-popup-anchor-right .maplibregl-popup-tip {
        border-left-color: #ffffff;
      }
      .dark .maplibregl-popup-anchor-right .maplibregl-popup-tip {
        border-left-color: #1c1c1e;
      }
      .maplibregl-popup-close-button {
        display: none;
      }      
    `;
    document.head.appendChild(style);
    return () => {
      const existingStyle = document.getElementById("fleet-marker-styles");
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const layerSwitcher = (
    <MapStylePicker
      currentStyle={currentStyle}
      onStyleChange={handleStyleChange}
      align="end"
      container={isFullscreen ? mapWrapperRef.current : undefined}
      buttonVariant="default"
      buttonSize="icon"
      className="h-8 w-8"
    />
  );

  const clusterToggle = (
    <Button
      variant={clusteringEnabled ? "default" : "ghost"}
      size="icon"
      className="h-8 w-8"
      onClick={() => setClusteringEnabled(!clusteringEnabled)}
      title={clusteringEnabled ? "Disable clustering" : "Enable clustering"}
    >
      <CircleDot className="h-4 w-4" />
    </Button>
  );

  const icons3dToggle = (
    <Button
      variant={use3DIcons ? "default" : "ghost"}
      size="icon"
      className="h-8 w-8"
      onClick={() => {
        const next = !use3DIconsRef.current;
        use3DIconsRef.current = next;
        setUse3DIcons(next);
        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];
        markerDeviceIdMap.current.clear();
        previousPositionsRef.current.clear();
      }}
      title={use3DIcons ? "Switch to simple icons" : "Switch to 3D icons"}
    >
      <Car className="h-4 w-4" />
    </Button>
  );

  const fullscreenButton = (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={toggleFullscreen}
      title={isFullscreen ? t('map.exitFullscreen') : t('map.fullscreen')}
    >
      {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
    </Button>
  );

  const editDialog = editingDevice && (
    <EditDeviceDialog
      device={editingDevice}
      groups={groups}
      open={editDialogOpen}
      onClose={() => setEditDialogOpen(false)}
    />
  );

  const handleShareConfirm = async () => {
    if (!shareDialogDevice) return;
    const expiration = new Date(shareExpiration);
    const expirationIso = expiration.toISOString();
    setShareDialogOpen(false);
    try {
      const response = await fetch(`${EDGE_FUNCTION_URL}/api/share/device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `deviceId=${shareDialogDevice.id}&expiration=${encodeURIComponent(expirationIso)}`,
      });
      if (response.ok) {
        const token = await response.text();
        await navigator.clipboard.writeText(
          `https://fleetmap-io.github.io/share-vehicle-location?token=${token}&expiration=${encodeURIComponent(expirationIso)}`
        );
      }
    } catch {
      // silently fail
    }
  };

  const shareDialog = (
    <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Share {shareDialogDevice?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <Label htmlFor="share-expiration">Expiration date & time</Label>
          <Input
            id="share-expiration"
            type="datetime-local"
            value={shareExpiration}
            min={new Date().toISOString().slice(0, 16)}
            onChange={e => setShareExpiration(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleShareConfirm} disabled={!shareExpiration}>{t("map.copyLink")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (compact) {
    return (
      <div ref={mapWrapperRef} className={`relative h-full w-full overflow-hidden bg-background ${className || ""}`}>
        <div ref={mapContainer} className="h-full w-full" />
        {controlsContainer
          ? createPortal(<>{layerSwitcher}{clusterToggle}{icons3dToggle}{fullscreenButton}</>, controlsContainer)
          : <div className="absolute top-3 right-3 z-10 flex gap-2">{layerSwitcher}{clusterToggle}{icons3dToggle}{fullscreenButton}</div>
        }
        {editDialog}
        {shareDialog}
      </div>
    );
  }

  return (
    <div ref={mapWrapperRef} className={`${isFullscreen ? 'h-screen w-screen' : 'h-full'}`}>
      <Card className={`flex flex-col transition-all duration-300 h-full ${isMaximized && !isFullscreen ? "fixed inset-4 z-50 h-auto" : "min-h-[400px]"} ${className || ""}`}>
        {isMaximized && !isFullscreen && <div className="fixed inset-0 bg-black/50 -z-10" onClick={() => setIsMaximized(false)} />}
        <CardHeader className="pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t('map.liveMap')}
            </CardTitle>
            <div className="flex items-center gap-2">
              {layerSwitcher}
              {clusterToggle}
              {fullscreenButton}
              {!isFullscreen && (
                <Button variant="outline" size="sm" onClick={() => setIsMaximized(!isMaximized)}>
                  {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 flex-1 min-h-0">
          <div ref={mapContainer} className="h-full w-full rounded-b-lg" />
        </CardContent>
      </Card>
      {editDialog}
      {shareDialog}
    </div>
  );
};
